import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, GraduationCap, Repeat, Users, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ACCOUNT_TYPES,
  MAX_ROLES,
  useAccountProfile,
  useSwitchRole,
  type AccountType,
} from "@/hooks/use-account-type";

const ICONS: Record<AccountType, typeof User> = { student: User, teacher: GraduationCap, parent: Users };

/** Selettore compatto del profilo attivo (visibile solo con più profili). */
export function RoleSwitcherMenu() {
  const { t } = useTranslation();
  const { active, roles } = useAccountProfile();
  const switchRole = useSwitchRole();
  if (roles.length < 2) return null;
  const ActiveIcon = ICONS[active];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" className="min-h-9 shrink-0 gap-1.5">
          <ActiveIcon className="h-4 w-4" aria-hidden />
          <span className="hidden max-w-[9rem] truncate sm:inline">{t(`teacher.role.${active}`)}</span>
          <Repeat className="h-3.5 w-3.5 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {roles.map((r) => {
          const Icon = ICONS[r];
          return (
            <DropdownMenuItem
              key={r}
              onSelect={() => {
                if (r === active) return;
                void switchRole(r)
                  .then(() => toast.success(t("roleui.switch.switched", { role: t(`teacher.role.${r}`) })))
                  .catch((e: unknown) => toast.error(e instanceof Error ? e.message : String(e)));
              }}
            >
              <Icon className="mr-2 h-4 w-4" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{t(`teacher.role.${r}`)}</span>
              {r === active && <Check className="ml-2 h-4 w-4 text-primary" aria-hidden />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Gestione completa dei profili dell'account (profilo → impostazioni). */
export function RoleManagerCard() {
  const { t } = useTranslation();
  const { active, roles, isAdult } = useAccountProfile();
  const queryClient = useQueryClient();
  const switchRole = useSwitchRole();
  const [saving, setSaving] = useState(false);

  const save = async (nextRoles: AccountType[], nextAdult: boolean) => {
    setSaving(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Not signed in");
      const nextActive = nextRoles.includes(active) ? active : nextRoles[0]!;
      const { error } = await supabase
        .from("profiles")
        .update({ account_roles: nextRoles, is_adult: nextAdult, account_type: nextActive })
        .eq("id", u.user.id);
      if (error) throw new Error(error.message);
      await queryClient.invalidateQueries();
      toast.success(t("roleui.switch.saved"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (r: AccountType) => {
    const has = roles.includes(r);
    if (has && roles.length === 1) return;
    if (!has && !isAdult) return toast.error(t("roleui.switch.minorHint"));
    if (!has && roles.length >= MAX_ROLES) return toast.error(t("roleui.switch.max"));
    void save(has ? roles.filter((x) => x !== r) : [...roles, r], isAdult);
  };

  return (
    <Card className="card-soft space-y-3 border-0 p-4">
      <div>
        <h2 className="text-sm font-semibold">{t("roleui.switch.title")}</h2>
        <p className="break-words text-xs text-muted-foreground">{t("roleui.switch.subtitle")}</p>
      </div>

      <div className="space-y-2">
        {ACCOUNT_TYPES.map((r) => {
          const Icon = ICONS[r];
          const on = roles.includes(r);
          return (
            <div
              key={r}
              className={`flex flex-wrap items-center gap-2 rounded-2xl border p-3 ${
                on ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-medium">{t(`teacher.role.${r}`)}</p>
                <p className="break-words text-xs text-muted-foreground">{t(`teacher.role.${r}Desc`)}</p>
              </div>
              {on && r !== active && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="min-h-9 shrink-0"
                  disabled={saving}
                  onClick={() => {
                    void switchRole(r)
                      .then(() => toast.success(t("roleui.switch.switched", { role: t(`teacher.role.${r}`) })))
                      .catch((e: unknown) => toast.error(e instanceof Error ? e.message : String(e)));
                  }}
                >
                  {t("roleui.switch.switchTo", { role: t(`teacher.role.${r}`) })}
                </Button>
              )}
              <Button
                size="sm"
                variant={on ? "ghost" : "default"}
                className="min-h-9 shrink-0"
                disabled={saving || (on && roles.length === 1)}
                onClick={() => toggle(r)}
              >
                {on ? t("common.remove", { defaultValue: "Rimuovi" }) : t("common.add", { defaultValue: "Aggiungi" })}
              </Button>
            </div>
          );
        })}
      </div>

      <label className="flex items-start gap-2 rounded-2xl bg-muted/60 p-3">
        <Checkbox
          checked={isAdult}
          disabled={saving}
          onCheckedChange={(v) => void save(roles, v === true)}
          aria-label={t("roleui.switch.adult")}
        />
        <span className="min-w-0">
          <span className="block break-words text-sm font-medium">{t("roleui.switch.adult")}</span>
          <span className="block break-words text-xs text-muted-foreground">{t("roleui.switch.addHint")}</span>
        </span>
      </label>
    </Card>
  );
}
